import { GraphErrorCode, isGraphErrorCode } from '../src/github/errors';
import { ContributionCalendar, DateWindow } from '../src/github/types';
import type { GraphType, RangeName } from '../src/graph/options';

export type FetchResult =
    | { ok: true; calendar: ContributionCalendar }
    | { ok: false; code: GraphErrorCode; aborted?: boolean };

/** Fetches contribution data from our own API. Never throws; errors become codes. */
export const fetchContributions = async (
    username: string,
    signal: AbortSignal,
    window?: DateWindow,
    /** Graph type/range at generation time, recorded by the server with `graph_generated`. */
    context?: { type: GraphType; range: RangeName },
): Promise<FetchResult> => {
    const params = new URLSearchParams({ username });
    if (context) {
        params.set('type', context.type);
        params.set('range', context.range);
    }
    if (window) {
        params.set('from', window.from);
        params.set('to', window.to);
    }
    let res: Response;
    try {
        res = await fetch(`/api/contributions?${params}`, {
            signal,
            headers: { Accept: 'application/json' },
        });
    } catch (error) {
        const aborted = (error as Error)?.name === 'AbortError';
        return { ok: false, code: 'UNAVAILABLE', aborted };
    }

    let body: unknown;
    try {
        body = await res.json();
    } catch {
        return { ok: false, code: res.status === 429 ? 'RATE_LIMITED' : 'INTERNAL' };
    }

    if (res.ok) return { ok: true, calendar: body as ContributionCalendar };

    const code = (body as { error?: { code?: unknown } })?.error?.code;
    return { ok: false, code: isGraphErrorCode(code) ? code : 'INTERNAL' };
};
